// Fetching "warnings on route", with the decisions the UI depends on kept
// here (and unit-tested with fakes) rather than in components:
//
// - A failure hides the warnings — never an error message — and is reported
//   through the site's handled-error reporter (component `route_alerts`), at
//   most once a minute per screen so an offline planner can't spam it.
// - A newer request wins: an older answer that arrives late is dropped, and
//   the planner aborts the request it no longer needs.
// - The planner asks ~1.5 s after the route stops changing (and only while no
//   routing request is pending), only for a route of two or more points, and
//   not again for a route it has just asked about. With the polylines go the
//   segments' hike ratings (`hikeRatings`, left out when there are none), and
//   a change of ratings alone is a different route to ask about.

import { GetRouteAlertsForHike, GetRouteAlertsForRoute } from '../graphql/route-alerts-graph';
import { EMPTY_ROUTE_ALERTS, RouteAlerts, normalizeRouteAlerts } from './routeAlertsModel';
import { RouteAlertsScreen } from './routeAlertsAnalytics';
import type { RouteAlertsRouteInput } from './routePolylines';

export interface GraphRequester {
  request(options: { document: any; variables?: Record<string, unknown>; signal?: AbortSignal }): Promise<any>;
}

export type ErrorReporter = (error: unknown, context: Record<string, unknown>) => void;

export const ERROR_REPORT_INTERVAL_MS = 60_000;

/** Reports at most one failure per interval. */
export class ThrottledErrorReporter {
  private lastAt: number | null = null;

  constructor(
    private readonly report: ErrorReporter,
    private readonly now: () => number = () => Date.now(),
    private readonly intervalMs = ERROR_REPORT_INTERVAL_MS,
  ) {}

  /** True when this failure was reported, false when throttled. */
  notify(error: unknown, context: Record<string, unknown>): boolean {
    const at = this.now();
    if (this.lastAt !== null && at - this.lastAt < this.intervalMs) return false;
    this.lastAt = at;
    try {
      this.report(error, { component: 'route_alerts', ...context });
    } catch {
      // Reporting must never break the page.
    }
    return true;
  }
}

export type RouteAlertsLoadResult =
  | { kind: 'loaded'; alerts: RouteAlerts }
  | { kind: 'failed'; error: unknown }
  | { kind: 'stale' };

/**
 * Requests for a hike's warnings in flight right now, per client. A map's
 * hike card and the warnings button beside the map's location control ask
 * about the same hike at the same moment: one request answers both.
 */
const inFlightForHike = new WeakMap<object, Map<string, Promise<any>>>();

function requestForHike(client: GraphRequester, hikeId: string): Promise<any> {
  if (typeof client !== 'object' || client === null) {
    return client.request({ document: GetRouteAlertsForHike, variables: { hikeId } });
  }
  let pending = inFlightForHike.get(client);
  if (!pending) {
    pending = new Map();
    inFlightForHike.set(client, pending);
  }
  const waiting = pending.get(hikeId);
  if (waiting) return waiting;
  const requests = pending;
  let sent: Promise<any>;
  try {
    sent = Promise.resolve(client.request({ document: GetRouteAlertsForHike, variables: { hikeId } }));
  } catch (error) {
    sent = Promise.reject(error);
  }
  const request = sent.finally(() => requests.delete(hikeId));
  requests.set(hikeId, request);
  return request;
}

/** The saved-hike warnings (overview page, map hike card). */
export class HikeRouteAlertsLoader {
  private latest = 0;
  private readonly reporter: ThrottledErrorReporter;

  constructor(
    private readonly client: GraphRequester,
    reportError: ErrorReporter,
    private readonly screen: RouteAlertsScreen,
    now?: () => number,
  ) {
    this.reporter = new ThrottledErrorReporter(reportError, now);
  }

  cancel(): void {
    this.latest += 1;
  }

  async load(hikeId: string | number): Promise<RouteAlertsLoadResult> {
    const ticket = ++this.latest;
    try {
      const data = await requestForHike(this.client, String(hikeId));
      if (ticket !== this.latest) return { kind: 'stale' };
      return { kind: 'loaded', alerts: normalizeRouteAlerts(data?.routeAlertsForHike) };
    } catch (error) {
      if (ticket !== this.latest) return { kind: 'stale' };
      this.reporter.notify(error, { operation: 'fetch_for_hike', hike_id: String(hikeId), screen: this.screen });
      return { kind: 'failed', error };
    }
  }
}

/* ---------- planner ---------- */

export const PLANNER_DEBOUNCE_MS = 1500;

/**
 * Planner mutations that never change the route. They must not restart the
 * debounce — the coordinates bar is set on every mouse move over the map, which
 * would otherwise hold the request off for as long as the pointer moves.
 * Anything else under planTrail/ counts as a possible route change (a repeat
 * of the same route is then skipped by its signature, so a false alarm is cheap).
 */
export const PLANNER_VIEW_ONLY_MUTATIONS: ReadonlySet<string> = new Set([
  'planTrail/setMiniCoordsBar',
  'planTrail/setZoom',
  'planTrail/setCenter',
  'planTrail/setFirstSymbolLayerId',
  'planTrail/setError',
  'planTrail/clearError',
  'planTrail/setTitle',
  'planTrail/setDescription',
  'planTrail/setSubmitForOfficialApproval',
  'planTrail/setMakeOfficial',
  'planTrail/setGenerateAiDescription',
  'planTrail/setAvailableFeatureTags',
  'planTrail/setAvailableActivityTags',
  'planTrail/setSelectedFeatureTags',
  'planTrail/setSelectedActivityTags',
  'planTrail/setAvailableCustomTags',
  'planTrail/setSelectedCustomTags',
  'planTrail/setDifficultyOverride',
  'planTrail/setRoutePreference',
  'planTrail/setPlanMode',
  'planTrail/toggleSnap',
]);

/** Whether a store mutation may have changed the planner's route. */
export function isPlannerRouteMutation(type: unknown): boolean {
  return typeof type === 'string' && type.startsWith('planTrail/') && !PLANNER_VIEW_ONLY_MUTATIONS.has(type);
}

export interface PlannerRouteAlertsDeps {
  client: GraphRequester;
  reportError: ErrorReporter;
  /**
   * The current route: polylines plus the hike ratings over their points
   * (empty polylines when it has no part of 2+ points). Preferred over `polylines`.
   */
  route?: () => RouteAlertsRouteInput;
  /** The current route as encoded polylines only (no ratings). */
  polylines?: () => string[];
  /** True while a routing (snap) request is pending: the route isn't settled. */
  isBusy: () => boolean;
  /** The result to show: EMPTY when hidden. */
  onResult: (alerts: RouteAlerts) => void;
  setTimer?: (fn: () => void, ms: number) => any;
  clearTimer?: (handle: any) => void;
  now?: () => number;
  createAbortController?: () => AbortController | null;
}

export class PlannerRouteAlertsController {
  private timer: any = null;
  private latest = 0;
  private inFlight: AbortController | null = null;
  private lastSignature: string | null = null;
  private readonly reporter: ThrottledErrorReporter;
  private readonly setTimer: (fn: () => void, ms: number) => any;
  private readonly clearTimer: (handle: any) => void;
  private readonly createAbortController: () => AbortController | null;
  private disposed = false;

  constructor(private readonly deps: PlannerRouteAlertsDeps) {
    this.reporter = new ThrottledErrorReporter(deps.reportError, deps.now);
    this.setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = deps.clearTimer ?? ((handle) => clearTimeout(handle));
    this.createAbortController = deps.createAbortController
      ?? (() => (typeof AbortController === 'undefined' ? null : new AbortController()));
  }

  /** The route changed (or routing finished): ask again once it has settled. */
  routeChanged(): void {
    if (this.disposed) return;
    if (this.timer !== null) this.clearTimer(this.timer);
    this.timer = this.setTimer(() => {
      this.timer = null;
      this.settled();
    }, PLANNER_DEBOUNCE_MS);
  }

  /** Ask again for the same route (a hazard on it has left the map). */
  forceRefresh(): void {
    this.lastSignature = null;
    this.routeChanged();
  }

  /** Stop: no timer, no request in flight, nothing more reported. */
  dispose(): void {
    this.disposed = true;
    if (this.timer !== null) this.clearTimer(this.timer);
    this.timer = null;
    this.abortInFlight();
    this.latest += 1;
  }

  private abortInFlight(): void {
    try {
      this.inFlight?.abort();
    } catch {
      // Nothing to abort.
    }
    this.inFlight = null;
  }

  /** Visible for tests: the debounce has elapsed. */
  async settled(): Promise<void> {
    if (this.disposed) return;
    // Still routing: the next change (routing done) restarts the debounce.
    if (this.deps.isBusy()) return;

    const input = this.currentRoute();
    const polylines = input.encodedPolylines;
    const hikeRatings = input.hikeRatings;
    const signature = `${polylines.join('\n')}\n${JSON.stringify(hikeRatings)}`;
    if (polylines.length === 0) {
      this.lastSignature = null;
      this.latest += 1;
      this.abortInFlight();
      this.deps.onResult(EMPTY_ROUTE_ALERTS);
      return;
    }
    if (signature === this.lastSignature) return;

    this.abortInFlight();
    const ticket = ++this.latest;
    const controller = this.createAbortController();
    this.inFlight = controller;
    this.lastSignature = signature;

    try {
      const data = await this.deps.client.request({
        document: GetRouteAlertsForRoute,
        variables: { encodedPolylines: polylines, ...(hikeRatings.length > 0 ? { hikeRatings } : {}) },
        ...(controller ? { signal: controller.signal } : {}),
      });
      if (ticket !== this.latest || this.disposed) return;
      this.inFlight = null;
      this.deps.onResult(normalizeRouteAlerts(data?.routeAlertsForRoute));
    } catch (error) {
      if (ticket !== this.latest || this.disposed) return;
      this.inFlight = null;
      // Try this route again on its next change rather than never.
      this.lastSignature = null;
      this.deps.onResult(EMPTY_ROUTE_ALERTS);
      this.reporter.notify(error, {
        operation: 'fetch_for_route',
        screen: 'planner',
        parts: polylines.length,
        rated_parts: hikeRatings.length,
      });
    }
  }

  private currentRoute(): RouteAlertsRouteInput {
    if (this.deps.route) {
      const input = this.deps.route();
      return { encodedPolylines: input?.encodedPolylines ?? [], hikeRatings: input?.hikeRatings ?? [] };
    }
    return { encodedPolylines: this.deps.polylines ? this.deps.polylines() : [], hikeRatings: [] };
  }
}
